import { useLayoutEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { Box, Segment } from './helpers';
import type { MatsProps } from './materials';
import type { TruckTextures } from './textures';
import { useConfig } from '../../store';
import { prefersReducedMotion } from '../../motion';
import { barZ, BULB_POSITIONS, festoonPoint } from './layout';

const AWNING_OPEN = -Math.PI * 0.6;
const AWNING_DAMPING = 7;
const AWNING_EPSILON = 1e-4;

function leafTexture(): THREE.CanvasTexture {
  const s = 128;
  const c = document.createElement('canvas');
  c.width = c.height = s;
  const g = c.getContext('2d')!;
  g.clearRect(0, 0, s, s);
  g.fillStyle = '#3a7d45';
  g.beginPath();
  g.ellipse(s / 2, s * 0.55, s * 0.24, s * 0.44, 0, 0, Math.PI * 2);
  g.fill();
  g.strokeStyle = '#2c5f34';
  g.lineWidth = 3;
  g.beginPath();
  g.moveTo(s / 2, s * 0.98);
  g.lineTo(s / 2, s * 0.12);
  g.stroke();
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

function Plant({ center, leafMat, potMat }: { center: [number, number, number]; leafMat: THREE.Material; potMat: THREE.Material }) {
  const leaves = useMemo(
    () =>
      Array.from({ length: 9 }, () => {
        const a = Math.random() * Math.PI * 2;
        const rad = Math.random() * 0.13;
        return {
          pos: [Math.cos(a) * rad, Math.random() * 0.2, Math.sin(a) * rad * 0.6] as [number, number, number],
          rot: [(Math.random() - 0.5) * 0.8, Math.random() * Math.PI, (Math.random() - 0.5) * 0.7] as [number, number, number],
          scale: 0.7 + Math.random() * 0.6,
        };
      }),
    [],
  );
  const leafGeo = useMemo(() => {
    const geo = new THREE.PlaneGeometry(0.12, 0.2);
    geo.translate(0, 0.1, 0);
    return geo;
  }, []);

  return (
    <group position={center}>
      <mesh position={[0, -0.06, 0]} material={potMat}>
        <boxGeometry args={[0.18, 0.12, 0.14]} />
      </mesh>
      {leaves.map((l, i) => (
        <mesh key={i} geometry={leafGeo} material={leafMat} position={l.pos} rotation={l.rot} scale={l.scale} />
      ))}
    </group>
  );
}

/** Open bar: counter, stocked bottles/glasses/plants, string lights, and the awning hatch. */
export function BarSide({ mats, tex }: MatsProps & { tex: TruckTextures | null }) {
  const showLights = useConfig((s) => s.showLights);
  const showBar = useConfig((s) => s.showBar);
  const showAwning = useConfig((s) => s.showAwning);

  const bottleGeo = useMemo(
    () =>
      new THREE.LatheGeometry(
        [
          [0, 0],
          [0.05, 0],
          [0.05, 0.17],
          [0.045, 0.22],
          [0.02, 0.27],
          [0.018, 0.34],
        ].map((p) => new THREE.Vector2(p[0], p[1])),
        16,
      ),
    [],
  );
  const coupeGeo = useMemo(
    () =>
      new THREE.LatheGeometry(
        [
          [0, 0],
          [0.008, 0.01],
          [0.008, 0.05],
          [0.035, 0.1],
          [0.033, 0.1],
          [0.006, 0.055],
          [0.006, 0.012],
          [0.03, 0.0],
        ].map((p) => new THREE.Vector2(p[0], p[1])),
        14,
      ),
    [],
  );
  const festoonGeo = useMemo(() => {
    const pts = Array.from({ length: 21 }, (_, i) => new THREE.Vector3(...festoonPoint(i / 20)));
    return new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 28, 0.005, 6);
  }, []);
  const bulbGeo = useMemo(() => new THREE.SphereGeometry(0.028, 12, 10), []);
  const leafMat = useMemo(() => new THREE.MeshStandardMaterial({ map: leafTexture(), roughness: 0.85, metalness: 0, side: THREE.DoubleSide, alphaTest: 0.5 }), []);

  // Awning open/closed, damped
  const pivotRef = useRef<THREE.Group>(null);
  const lastStep = useRef<number | null>(null);
  const invalidate = useThree((s) => s.invalidate);
  useLayoutEffect(() => {
    if (pivotRef.current) pivotRef.current.rotation.x = showAwning ? AWNING_OPEN : 0;
    // run once on mount to avoid a swing-in
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useLayoutEffect(() => {
    invalidate();
  }, [showAwning, invalidate]);
  useFrame(() => {
    const pivot = pivotRef.current;
    if (!pivot) return;
    const target = showAwning ? AWNING_OPEN : 0;
    if (pivot.rotation.x === target) {
      lastStep.current = null;
      return;
    }
    const now = performance.now();
    const dt = lastStep.current === null ? 0 : (now - lastStep.current) / 1000;
    lastStep.current = now;
    const next = prefersReducedMotion() ? target : THREE.MathUtils.damp(pivot.rotation.x, target, AWNING_DAMPING, dt);
    pivot.rotation.x = Math.abs(next - target) < AWNING_EPSILON ? target : next;
    if (pivot.rotation.x !== target) invalidate();
  });

  // Gas struts, computed for the open pose
  const struts = useMemo(() => {
    const theta = AWNING_OPEN;
    const edgeY = 1.7 + -0.86 * Math.cos(theta);
    const edgeZ = barZ + -0.86 * Math.sin(theta);
    return [1, -1].map((zs) => ({
      a: [-0.8 + zs * 0.6, 1.58, barZ + 0.02] as [number, number, number],
      b: [-0.8 + zs * 0.55, edgeY, edgeZ] as [number, number, number],
    }));
  }, []);

  const bottleMats = [mats.glassGreen, mats.glassClear, mats.glassAmber, mats.glassClear, mats.glassGreen];

  return (
    <group>
      {/* Opening recess + chrome frame */}
      <Box args={[1.46, 0.86, 0.02]} mat={mats.cabinDark} pos={[-0.8, 1.24, barZ + 0.004]} />
      {(
        [
          [1.5, 0.045, 0, 1.7],
          [1.5, 0.045, 0, 0.8],
          [0.045, 0.9, -0.75, 1.24],
          [0.045, 0.9, 0.75, 1.24],
        ] as const
      ).map((f, i) => (
        <Box key={i} args={[f[0], f[1], 0.05]} mat={mats.chromeHi} pos={[-0.8 + f[2], f[3], barZ + 0.012]} />
      ))}

      {/* Counter */}
      <Box args={[1.5, 0.06, 0.32]} mat={mats.wood} pos={[-0.8, 0.86, barZ + 0.15]} />
      <Box args={[1.5, 0.34, 0.04]} mat={mats.counter} pos={[-0.8, 0.68, barZ + 0.31]} />

      {/* Stocked bar */}
      {showBar && (
        <group>
          {bottleMats.map((m, i) => (
            <mesh key={i} geometry={bottleGeo} material={m} position={[-1.34 + i * 0.16, 0.9, barZ + 0.14]} />
          ))}
          {Array.from({ length: 4 }, (_, i) => (
            <mesh key={i} geometry={coupeGeo} material={mats.glassClear} position={[-0.5 + i * 0.2, 0.92, barZ + 0.22]} />
          ))}
          <Plant center={[-1.42, 0.92, barZ + 0.16]} leafMat={leafMat} potMat={mats.black} />
          <Plant center={[-0.2, 0.92, barZ + 0.16]} leafMat={leafMat} potMat={mats.black} />
        </group>
      )}

      {/* String lights */}
      {showLights && (
        <group>
          <mesh geometry={festoonGeo} material={mats.black} />
          {BULB_POSITIONS.map((p, i) => (
            <mesh key={i} geometry={bulbGeo} material={mats.bulb} position={p} />
          ))}
        </group>
      )}

      {/* Awning hatch */}
      <group ref={pivotRef} position={[-0.8, 1.7, barZ]}>
        <mesh position={[0, -0.45, 0]} material={mats.hatchPaint}>
          <boxGeometry args={[1.5, 0.9, 0.05]} />
        </mesh>
        {tex && (
          <mesh position={[0, -0.45, -0.031]} rotation={[0, Math.PI, Math.PI]}>
            <planeGeometry args={[1.0, 1.0 / tex.logoAspect]} />
            <meshStandardMaterial map={tex.logoTex} transparent roughness={0.5} />
          </mesh>
        )}
      </group>
      {showAwning && struts.map((s, i) => <Segment key={i} a={s.a} b={s.b} r={0.012} mat={mats.chrome} />)}
    </group>
  );
}
